import { MEMBERSHIP_SEASON } from '../config/membershipSeason';
import { getMembershipFlagsForSeason } from './membershipFlags';

const EUROPEAN_COMPETITIONS = new Set([
  'Champions League',
  'UEFA Europa League',
  'UEFA Europa Conference League',
]);

const STAR_EUROPEAN_SELF_ORDER_MIN_INDEX = 5;
const STAR_COPPA_SELF_ORDER_MIN_INDEX = 2;

export const SUBSCRIBER_NOTIFY_COMPETITIONS = new Set([
  'Serie A',
  'Champions League',
  'UEFA Europa League',
  'UEFA Europa Conference League',
  'Coppa Italia',
]);

export function supportsSubscriberNotify(ticketOrForm) {
  return Boolean(
    ticketOrForm?.isHome && SUBSCRIBER_NOTIFY_COMPETITIONS.has(ticketOrForm?.competition),
  );
}

export function getSubscriberType(flags) {
  if (!flags) return null;
  if (flags.star || flags.starAboClub) return 'STAR';
  if (flags.jofcAbonnesFull) return 'FULL';
  if (flags.base) return 'BASE';
  return null;
}

export function isClubOwnedStarSubscription(user, flags) {
  return Boolean(flags?.starAboClub || user?.subscription?.isClubOwned);
}

export function isStadiumSubscriber(user, season = MEMBERSHIP_SEASON) {
  if (!user?.subscription?.has) return false;
  const flags = getMembershipFlagsForSeason(user, season);
  return Boolean(getSubscriberType(flags));
}

export function isEuropeanCompetition(competition) {
  return EUROPEAN_COMPETITIONS.has(competition);
}

export function getEuropeanHomeMatchIndex(ticket) {
  if (ticket?.europeanHomeMatchIndex != null && ticket.europeanHomeMatchIndex >= 1) {
    return ticket.europeanHomeMatchIndex;
  }
  if (ticket?.competitionPhase === 'after_group_stage') {
    return STAR_EUROPEAN_SELF_ORDER_MIN_INDEX;
  }
  if (ticket?.competitionPhase === 'group_stage') {
    return 1;
  }
  return null;
}

export function getCoppaHomeMatchIndex(ticket) {
  if (ticket?.coppaMatchNumber != null && ticket.coppaMatchNumber >= 1) {
    return ticket.coppaMatchNumber;
  }
  return 1;
}

export function requiresPrimaryTicketForSubscriber(subscriberType, ticket, { isClubOwned = false } = {}) {
  if (!subscriberType) return true;

  if (ticket?.isHome === false) {
    return true;
  }

  if (subscriberType === 'STAR' && isClubOwned) {
    return true;
  }

  const competition = ticket?.competition;

  if (competition === 'Serie A') {
    return false;
  }

  switch (subscriberType) {
    case 'BASE':
    case 'FULL':
      return true;

    case 'STAR':
      if (competition === 'Coppa Italia') {
        return getCoppaHomeMatchIndex(ticket) >= STAR_COPPA_SELF_ORDER_MIN_INDEX;
      }
      if (isEuropeanCompetition(competition)) {
        const europeanIndex = getEuropeanHomeMatchIndex(ticket);
        if (europeanIndex == null) {
          return false;
        }
        return europeanIndex >= STAR_EUROPEAN_SELF_ORDER_MIN_INDEX;
      }
      return true;

    default:
      return true;
  }
}

export function showPrimaryTicketSection(user, ticket, season = MEMBERSHIP_SEASON) {
  if (!user) return true;
  if (!isStadiumSubscriber(user, season)) return true;

  const flags = getMembershipFlagsForSeason(user, season);
  const subscriberType = getSubscriberType(flags);
  const isClubOwned = isClubOwnedStarSubscription(user, flags);
  return requiresPrimaryTicketForSubscriber(subscriberType, ticket, { isClubOwned });
}

export function getTicketOrderContext(user, ticket, season = MEMBERSHIP_SEASON) {
  const flags = user ? getMembershipFlagsForSeason(user, season) : null;
  const subscriberType = getSubscriberType(flags);
  const isClubOwned = user ? isClubOwnedStarSubscription(user, flags) : false;
  const isSubscriber = Boolean(subscriberType) && Boolean(user?.subscription?.has);
  const showPrimaryTicket = user && ticket
    ? requiresPrimaryTicketForSubscriber(subscriberType, ticket, { isClubOwned })
    : true;

  return {
    season,
    subscriberType,
    isSubscriber,
    isClubOwned,
    showPrimaryTicket,
    requiresPrimaryTicket: showPrimaryTicket,
    blocksApplyToCompanions: false,
  };
}

export function formatDateOfBirthForInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export function buildPrimaryAttendeeFromUser(user, seasonCodes = {}) {
  return {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    dateOfBirth: formatDateOfBirthForInput(user?.dateOfBirth),
    juventusCard: user?.juventusFanCard?.number || '',
    country: user?.address?.country || '',
    city: user?.address?.city || '',
    email: user?.email || '',
    phone: user?.phone || '',
    ecardCode: seasonCodes.ecardCode || '',
    pnrCode: seasonCodes.pnrCode || '',
  };
}

export function emptyCompanionAttendee() {
  return {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    juventusCard: '',
    country: '',
    city: '',
    email: '',
    phone: '',
    ecardCode: '',
    ecardVerified: false,
  };
}

export function buildCompanionAttendeeFromProfile(profile) {
  return {
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    dateOfBirth: profile?.dateOfBirth || '',
    juventusCard: profile?.juventusCard || '',
    country: profile?.country || '',
    city: profile?.city || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    ecardCode: profile?.ecardCode || '',
    ecardVerified: true,
  };
}

export function isCompanionAttendeeComplete(attendee) {
  return Boolean(
    attendee?.ecardVerified
    && attendee?.firstName?.trim()
    && attendee?.lastName?.trim()
    && attendee?.dateOfBirth?.trim()
    && attendee?.country?.trim()
    && attendee?.city?.trim()
    && attendee?.email?.trim()
    && attendee?.phone?.trim()
    && attendee?.ecardCode?.trim(),
  );
}
